import * as R from 'ramda';
import { parseScript } from 'esprima';
import { upperFirstLetter, isBlank, flattenObject, rejectEmptyValues } from '@trs/utils';
import { getError } from 'config/errors';
import { TextOnly } from 'modules/common/Text';

// Eligibility validation mirrors modules/userManagement/components/eligibility (reused for Eligible Population)
const validateEligibilityFields = (ruleObject) =>
  R.mapObjIndexed((item, key) => {
    let keyLabel;
    switch (key) {
      case 'variable':
        keyLabel = TextOnly({ path: 'onboarding.ELIGIBILITY_RULE_VARIABLE_NAME' });
        break;
      case 'operator':
        keyLabel = TextOnly({ path: 'onboarding.ELIGIBILITY_RULE_OPERATOR' });
        break;
      case 'values':
        keyLabel = TextOnly({ path: 'onboarding.ELIGIBILITY_RULE_VALUE' });
        break;
      default:
        keyLabel = key;
        break;
    }

    return getError(TextOnly({ path: 'generic.ERROR_REQUIRED' }), upperFirstLetter(keyLabel));
  }, R.filter(R.isEmpty, ruleObject));

export const getEligibilityErrors = (eligibility, errors = {}) => {
  const errorObject = errors;
  if (eligibility.eligibilityRows) {
    R.forEach((row) => getEligibilityErrors(row, errorObject), eligibility.eligibilityRows);
  }

  if (!isBlank(R.prop('eligibilityRowsOperator', eligibility))) {
    return errorObject;
  }

  const ruleErrors = validateEligibilityFields({
    variable: R.prop('variable', eligibility),
    operator: R.prop('operator', eligibility),
    values: R.prop('values', eligibility),
  });

  if (!isBlank(ruleErrors)) {
    errorObject[eligibility.uid] = ruleErrors;
  }

  return errorObject;
};

export const validatePopulation = (population) => {
  const eligibilityErrors = getEligibilityErrors(R.propOr({}, 'eligibility', population));
  return isBlank(rejectEmptyValues(R.values(eligibilityErrors)))
    ? {}
    : { eligibility: eligibilityErrors };
};

export const validatePopulations = (populations) => populations.map(validatePopulation);

// Formula validation mirrors modules/rewards/components/rulesBuilder/helpers (reused for Bonus Compensation/Midpoint)
const getFormulaErrors = (formula) => {
  const errors = {};
  const flattened = flattenObject(formula);
  R.forEach((item) => {
    if (item.indexOf('value') !== -1 && R.isEmpty(flattened[item])) {
      errors[item.replace('.value', '')] = true;
    }
  }, R.keys(flattened));

  return errors;
};

const getFormulaString = (formula) =>
  formula
    .map((item) => {
      if (item.type === 'variable' || item.type === 'numeric' || item.type === 'percentage') {
        return `"${item.value}"`;
      }
      if (item.type === 'function') {
        const firstArgumentFormula = getFormulaString(item.arguments[0]);
        const secondArgumentFormula = getFormulaString(item.arguments[1]);
        return `${item.value}(${firstArgumentFormula}, ${secondArgumentFormula})`;
      }
      return item.value;
    })
    .join('');

const syntaxValidateFormula = (formulaString) => {
  try {
    parseScript(formulaString);
    return {};
  } catch (error) {
    return { generic: TextOnly({ path: 'onboarding.RULES_BUILDER_INVALID_FORMULA_ERROR_LABEL' }) };
  }
};

export const validateFormula = (formula) => {
  if (R.isEmpty(formula)) {
    return { generic: TextOnly({ path: 'onboarding.RULES_BUILDER_EMPTY_FORMULA_ERROR_LABEL' }) };
  }

  const formulaErrors = getFormulaErrors(formula);
  return R.isEmpty(formulaErrors) ? syntaxValidateFormula(getFormulaString(formula)) : formulaErrors;
};

export const validateRule = (rule) => {
  const eligibilityErrors = getEligibilityErrors(R.propOr({}, 'eligibility', rule));
  const formulaErrors = validateFormula(R.propOr([], 'formula', rule));
  const errors = {};

  if (!isBlank(rejectEmptyValues(R.values(eligibilityErrors)))) {
    errors.eligibility = eligibilityErrors;
  }

  if (!isBlank(formulaErrors)) {
    errors.formula = formulaErrors;
  }

  return errors;
};

export const validateRules = (rules) => rules.map(validateRule);

export const hasErrors = (errorsList) =>
  errorsList.some((errors) => !isBlank(rejectEmptyValues(R.values(errors))));

// Disclaimer validation mirrors modules/flexibleRewards/components/createPackage/emailAndDisclaimer (reused for Bonus Midpoint)
export const validateDisclaimer = ({ includeDisclaimer, disclaimer }) => {
  if (includeDisclaimer && isBlank(disclaimer)) {
    return TextOnly({ path: 'generic.ERROR_REQUIRED' }).replace(
      '%FIELD%',
      TextOnly({ path: 'onboarding.BONUS_MODELLER_DISCLAIMER' })
    );
  }
  return '';
};
